import React, {useState} from 'react';
import {useParams} from 'react-router-dom';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {useMutation, useQuery} from '@apollo/client';
import {Grid, List, ListItem, ListItemText, useTheme} from '@mui/material';
import {themeColors} from '../../theme/Theme';
import {HandoverStep} from '../../components/HandoverStep/HandoverStep';
import {HandoverFooterNavigation} from '../../components/FooterNavigation/HandoverFooterNavigation';
import {Panel} from '../../components/Panel/Panel';
import {HandoverNotes} from './HandoverNotes';
import {
  UpdateEditedNotes,
  UpdateEditedNotesVariables,
} from '../../schema-types/UpdateEditedNotes';
import useTitle from '../../hooks/useTitle';
import {useReviewContext} from '../../contexts/ReviewContext';
import {useAccountContext} from '../../contexts/AccountContext';
import Loading from '../../components/Loading/Loading';
import {useHandoverNoteStyles} from '../../features/notes/handoverNoteStyles';
import {
  GetHandoverNotes,
  GetHandoverNotesVariables,
} from '../../schema-types/GetHandoverNotes';
import validateUUID from '../../utils/validateUUID';
import {
  GET_HANDOVER_NOTES_QUERY,
  UPDATE_EDITED_NOTES_MUTATION,
} from '../../features/notes/notesQueries';
import {PreviousNotes} from '../../features/notes/PreviousNotes';
import {HandoverNoteFields, notesOrder} from './HandoverNotesHelpers';
import {ErrorPage} from '../ErrorPage';

export const HandoverNotesPage: React.FC = () => {
  const params = useParams();
  const {reviewDetails} = useReviewContext();
  useTitle(`Review notes - ${reviewDetails?.account?.name}`);
  const reviewId = params.reviewId as string;
  const [onUpdateNotes, {loading: updateNotesLoading}] = useMutation<
    UpdateEditedNotes,
    UpdateEditedNotesVariables
  >(UPDATE_EDITED_NOTES_MUTATION, {
    variables: {
      reviewId: validateUUID(reviewId)!,
    },
  });
  const {isFullySubmitted} = useReviewContext();

  if (!reviewDetails) {
    return <ErrorPage code="404" text="Review not found" />;
  }

  return (
    <>
      <HandoverStep />
      <Panel title="Notes" color={themeColors.blue}>
        <HandoverNotes
          reviewId={reviewId}
          readOnly={!reviewDetails?.isDraft || isFullySubmitted}
        />
      </Panel>
      {/* this prevents the footer from obscuring the contents above */}

      <HandoverFooterNavigation
        onNext={onUpdateNotes}
        onExit={onUpdateNotes}
        exitingLoading={updateNotesLoading}
        onNextLoading={updateNotesLoading}
        nextIcon={<TrendingUpIcon />}
      />
    </>
  );
};

export const ClientNotesPageWithCurrentReview: React.FC<{
  reviewId: string;
}> = ({reviewId}) => {
  const {reviewDetails} = useReviewContext();
  useTitle(`Review notes - ${reviewDetails?.account?.name}`);
  return (
    <>
      <Panel title="Notes" color={themeColors.blue} style={{marginBottom: 160}}>
        <HandoverNotes reviewId={reviewId} readOnly={!reviewDetails?.isDraft} />
      </Panel>
    </>
  );
};

export const ClientNotesPreviousNotesOnly: React.FC = () => {
  const {reviewDetails} = useReviewContext();
  const {account, accountId} = useAccountContext();
  const classes = useHandoverNoteStyles();
  const theme = useTheme();
  const detailsType: HandoverNoteFields = account?.isTrust
    ? 'General details'
    : 'Personal details';
  const [currentNotesType, setCurrentNotesType] =
    useState<HandoverNoteFields>(detailsType);
  const {data} = useQuery<GetHandoverNotes, GetHandoverNotesVariables>(
    GET_HANDOVER_NOTES_QUERY,
    {
      variables: {
        accountId: validateUUID(accountId)!,
        valueId: validateUUID(reviewDetails?.id) ?? 'no review ID',
        keyId: 'review_id',
        contactId: validateUUID(reviewDetails?.contact?.id),
      },
      skip: !reviewDetails || !accountId,
    }
  );
  useTitle(`Review notes - ${account?.name}`);
  if (!data?.previousNotes) {
    return <Loading />;
  }
  return (
    <>
      <Panel title="Notes" color={themeColors.blue} style={{marginBottom: 160}}>
        <Grid container spacing={1} className="fixed-footer-gap">
          <Grid item xs={2}>
            <List>
              {notesOrder.map((noteTypeItem) => {
                const active = currentNotesType === noteTypeItem;
                return (
                  <ListItem
                    key={noteTypeItem}
                    sx={{
                      ...(currentNotesType === noteTypeItem ? classes.activeItem : {}),
                      ...classes.rowItem,
                    }}
                    onClick={() => setCurrentNotesType(noteTypeItem)}
                  >
                    <ListItemText
                      primaryTypographyProps={{
                        style: {
                          paddingLeft: theme.spacing(2),
                          paddingBottom: 0,
                          fontWeight: active ? 600 : 500,
                          fontSize: 14,
                          color: theme.palette.primary.main,
                          userSelect: 'none',
                          display: 'flex',
                        },
                      }}
                      sx={classes.itemText}
                    >
                      {noteTypeItem}
                    </ListItemText>
                  </ListItem>
                );
              })}
            </List>
          </Grid>
          <Grid item xs={10} style={{height: '100%', overflowY: 'scroll'}}>
            <PreviousNotes
              page={currentNotesType}
              allPreviousNotes={data.previousNotes}
            />
          </Grid>
        </Grid>
      </Panel>
    </>
  );
};
